/**
 * AdminResources - Refactored main page component
 * Manages vault resources with upload, edit, and delete functionality
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth, API } from "@/App";
import { AdminNavigation } from "@/components/layout/Navigation";
import axios from "axios";
import { toast } from "sonner";

// Refactored components
import { ResourceUploadDialog } from "@/components/admin/ResourceUploadDialog";
import { ResourceEditDialog } from "@/components/admin/ResourceEditDialog";
import { ResourceTable } from "@/components/admin/ResourceTable";
import { ResourceFilters } from "@/components/admin/ResourceFilters";

export default function AdminResources() {
  const { token } = useAuth();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterProgram, setFilterProgram] = useState("");
  const [filterLibrary, setFilterLibrary] = useState("");
  
  // Edit dialog state
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  
  // Ref to trigger upload dialog from table
  const uploadDialogRef = useRef(null);

  const fetchResources = useCallback(async () => {
    try {
      const params = {};
      if (filterProgram) params.program = filterProgram;
      if (filterLibrary) params.vault_chamber = filterLibrary;
      if (searchQuery) params.search = searchQuery;
      
      const res = await axios.get(`${API}/admin/resources`, {
        withCredentials: true,
        params
      });
      setResources(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      if (error.response?.status !== 401) {
        toast.error("Failed to load resources");
      }
    } finally {
      setLoading(false);
    }
  }, [filterProgram, filterLibrary, searchQuery]);

  useEffect(() => {
    fetchResources();
  }, [fetchResources]);

  const handleEdit = (resource) => {
    setEditingResource(resource);
    setIsEditOpen(true);
  };

  const handleEditClose = (open) => {
    setIsEditOpen(open);
    if (!open) {
      setEditingResource(null);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <AdminNavigation />
      
      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-heading text-foreground mb-2">Vault Resources</h1>
            <p className="text-muted-foreground">
              Upload and manage PDFs, worksheets, and downloadable resources for the Vault
            </p>
          </div>
          
          <ResourceUploadDialog 
            ref={uploadDialogRef}
            token={token} 
            onUploadSuccess={fetchResources} 
          />
        </div>

        {/* Filters */}
        <ResourceFilters
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          filterProgram={filterProgram}
          onFilterProgramChange={setFilterProgram}
          filterLibrary={filterLibrary}
          onFilterLibraryChange={setFilterLibrary}
        />

        {/* Resources Table */}
        <ResourceTable
          token={token}
          resources={resources}
          loading={loading}
          searchQuery={searchQuery}
          filterProgram={filterProgram}
          filterLibrary={filterLibrary}
          onEdit={handleEdit}
          onRefresh={fetchResources}
          onOpenUpload={() => {
            // Trigger the upload dialog - handled by the Dialog component
            document.querySelector('[data-testid="upload-resource-btn"]')?.click();
          }}
        />

        {/* Summary */}
        <div className="mt-6 text-center text-white/50 text-sm">
          {resources.length} resource{resources.length !== 1 ? 's' : ''} total
        </div>
      </main>

      {/* Edit Dialog */}
      <ResourceEditDialog
        token={token}
        resource={editingResource}
        isOpen={isEditOpen}
        onOpenChange={handleEditClose}
        onEditSuccess={fetchResources}
      />
    </div>
  );
}
